import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getCommerceId } from '@/lib/ownership';
import {
  getOrganizationByCommerce,
  listOrganizationMemberships,
  fetchUserLabels,
  type CoreMembership,
  type CoreOrganization,
  type UserLabel,
} from '@/lib/zifekCore';
import ActivityLogTab from '@/pages/dashboard/users/components/ActivityLogTab';

/**
 * Onglet « Journal » du dashboard marchand — accès rapide au journal
 * d'activité de l'équipe (qui a fait quoi, quand, y compris les
 * suppressions), sans passer par la Gestion d'utilisateurs.
 */
export default function DashboardJournalPage() {
  const { user } = useAuth();
  const commerceId = useMemo(() => getCommerceId(user), [user]);
  const [org, setOrg] = useState<CoreOrganization | null>(null);
  const [memberships, setMemberships] = useState<CoreMembership[]>([]);
  const [users, setUsers] = useState<Record<number, UserLabel>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    if (!commerceId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const organization = await getOrganizationByCommerce(commerceId);
      setOrg(organization);
      if (!organization) { setLoading(false); return; }
      const memb = await listOrganizationMemberships(organization.id);
      setMemberships(memb);
      const labels = await fetchUserLabels([...memb.map((m) => m.user_id), organization.owner_user_id || 0]);
      setUsers(labels);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Impossible de charger le journal d’activité.');
    } finally {
      setLoading(false);
    }
  }, [commerceId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center gap-2">
            <i className="ri-history-line text-primary-600"></i>
            Journal d&apos;activité
          </h2>
          <p className="text-sm text-foreground-500 mt-1">
            Tout ce que fait votre équipe, en un coup d&apos;œil — créations, modifications et suppressions, avec l&apos;auteur et l&apos;heure.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2.5 bg-background-100 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-background-200/70 disabled:opacity-50 transition-colors"
        >
          <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`}></i>
          Actualiser
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-24"><i className="ri-loader-4-line animate-spin text-3xl text-primary-500"></i></div>
      ) : error ? (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-center justify-between gap-3">
          <span className="flex items-center gap-2"><i className="ri-error-warning-line"></i>{error}</span>
          <button onClick={load} className="text-xs underline cursor-pointer whitespace-nowrap">Réessayer</button>
        </div>
      ) : !org ? (
        <div className="flex flex-col items-center justify-center py-20 bg-background-50 border border-background-200/70 rounded-lg text-center px-6">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
            <i className="ri-building-2-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-700 font-medium">Votre organisation n&apos;est pas encore initialisée.</p>
          <p className="text-sm text-foreground-500 mt-1 max-w-md">Le socle de votre entreprise est normalement créé automatiquement. Contactez le support si le problème persiste.</p>
          <button onClick={load} className="mt-4 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap">Réessayer</button>
        </div>
      ) : (
        <ActivityLogTab org={org} users={users} memberships={memberships} />
      )}
    </div>
  );
}